/**
 * generateHtmlJson_convert.ts - Convert HtmlJson to HTML
 * Port of C# GenerateHtmlJson_Convert.cs
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';

// Type prefixes for new format
const TYPE_HTML = 's';           // Static HTML
const TYPE_JSONKEY = 'k';        // JSON key {{$Key}}
const TYPE_JSONKEY_SKIP = 'K';   // Skipped JSON key {{!$Key}}
const TYPE_PLACEHOLDER = 'p';    // Placeholder {{Name}}
const TYPE_PLACEHOLDER_SKIP = 'P'; // Skipped placeholder {{!Name}}
const TYPE_SLOTHOLDER = 'h';     // Slotholder {{#Name}}
const TYPE_SLOTHOLDER_SKIP = 'H'; // Skipped slotholder {{!#Name}}
const TYPE_ARRAYBLOCK = 'a';     // Array block {{@Name}}
const TYPE_ARRAYBLOCK_SKIP = 'A'; // Skipped array block {{!@Name}}
const TYPE_SLOTHTML = 'x';       // HTML within slot
const TYPE_SLOT = 'i';           // Slot injection point {{$HTMLPLACEHOLDER}}
const TYPE_CONDITIONAL = 'c';    // Conditional block inside array

const META_KEY = '_meta';

/**
 * Extracts the name from a key (e.g., "k2_Title" -> "Title")
 */
function getKeyName(key: string): string {
    const underscoreIdx = key.indexOf('_');
    return underscoreIdx >= 0 ? key.substring(underscoreIdx + 1) : '';
}

/**
 * Converts HtmlJson structure back to HTML string (without resolving placeholders)
 * Uses new format with type prefixes
 */
export function convertJsonToHtml(htmlJson: JsonObject): string {
    if (!htmlJson) {
        return '';
    }

    let result = '';
    for (const key of htmlJson.keys()) {
        if (key === META_KEY) continue; // Skip metadata

        const value = htmlJson.get(key);
        result += convertKeyToHtml(key, value);
    }

    return result;
}

/**
 * Converts a key/value to HTML using type prefix
 */
function convertKeyToHtml(key: string, value: JsonValue): string {
    if (!key || key.length === 0) return '';

    const type = key[0];
    const name = getKeyName(key);

    switch (type) {
        case TYPE_HTML:
        case TYPE_SLOTHTML:
            return value.asString() ?? '';

        case TYPE_JSONKEY:
            return `{{$${name}}}`;

        case TYPE_JSONKEY_SKIP:
            return `{{!$${name}}}`;

        case TYPE_PLACEHOLDER:
            return `{{${name}}}`;

        case TYPE_PLACEHOLDER_SKIP:
            return `{{!${name}}}`;

        case TYPE_SLOT:
            // Slot injection point {{$HTMLPLACEHOLDER}}
            return `{{$${name}}}`;

        case TYPE_SLOTHOLDER:
        case TYPE_SLOTHOLDER_SKIP: {
            const slotObj = value.asObject();
            if (slotObj) {
                return convertSlotholderToHtml(slotObj, type === TYPE_SLOTHOLDER_SKIP);
            }
            break;
        }

        case TYPE_ARRAYBLOCK:
        case TYPE_ARRAYBLOCK_SKIP: {
            const arrayObj = value.asObject();
            if (arrayObj) {
                return convertArrayBlockToHtml(name, arrayObj, type === TYPE_ARRAYBLOCK_SKIP);
            }
            break;
        }

        case TYPE_CONDITIONAL: {
            const condObj = value.asObject();
            if (condObj) {
                return convertConditionalBlockToHtml(condObj);
            }
            break;
        }
    }

    return '';
}

/**
 * Converts slotholder structure to HTML
 */
function convertSlotholderToHtml(slotStructure: JsonObject, isSkip: boolean): string {
    let result = '';
    const slotName = slotStructure.get('SlotName').asString() ?? '';

    // Open tag
    result += isSkip ? '{{!#' : '{{#';
    result += slotName;
    result += '}}';

    // Process slot content
    for (const slotKey of slotStructure.keys()) {
        if (slotKey === 'SlotName' || slotKey === 'IsSkipMarked' || slotKey === META_KEY) continue;

        const slotValue = slotStructure.get(slotKey);

        // SlotHtml entry (x prefix)
        if (slotKey.length > 0 && slotKey[0] === TYPE_SLOTHTML) {
            result += slotValue.asString() ?? '';
        } else {
            const nestedObj = slotValue.asObject();
            if (nestedObj) {
                // Direct HTMLPLACEHOLDER key (e.g., "HTMLPLACEHOLDER", "HTMLPLACEHOLDER1")
                result += '{{@';
                result += slotKey;
                result += '}}';
                result += convertJsonToHtml(nestedObj);
                result += '{{/';
                result += slotKey;
                result += '}}';
            }
        }
    }

    // Close tag
    result += '{{/';
    result += slotName;
    result += '}}';

    return result;
}

/**
 * Converts array block structure to HTML
 */
function convertArrayBlockToHtml(arrayKey: string, arrayBlock: JsonObject, isSkip: boolean): string {
    let result = '';

    const mainBlockValue = arrayBlock.get('MainBlock');
    if (!mainBlockValue.isNull) {
        result += isSkip ? '{{!@' : '{{@';
        result += arrayKey;
        result += '}}';
        const mainBlockObj = mainBlockValue.asObject();
        if (mainBlockObj) {
            result += convertJsonToHtml(mainBlockObj);
        }
        result += '{{/';
        result += arrayKey;
        result += '}}';
    }

    // Output whitespace between positive and negative blocks if present
    const whitespaceBetween = arrayBlock.get('WhitespaceBetween').asString();
    if (whitespaceBetween) {
        result += whitespaceBetween;
    }

    const negativeBlockValue = arrayBlock.get('NegativeBlock');
    if (!negativeBlockValue.isNull) {
        result += '{{^';
        result += arrayKey;
        result += '}}';
        const negBlockObj = negativeBlockValue.asObject();
        if (negBlockObj) {
            result += convertJsonToHtml(negBlockObj);
        }
        result += '{{/';
        result += arrayKey;
        result += '}}';
    }

    return result;
}

/**
 * Converts conditional block structure to HTML
 */
function convertConditionalBlockToHtml(conditionalBlock: JsonObject): string {
    let result = '';
    const conditionalKey = conditionalBlock.get('ConditionalKey').asString() ?? '';
    const closeTagHasSpace = conditionalBlock.get('CloseTagHasSpace').asString() === 'true';

    const isNegative = conditionalBlock.get('IsNegative').asString() === 'true';
    result += isNegative ? '{{^' : '{{?';
    result += conditionalKey;
    result += '}}';

    const contentObj = conditionalBlock.get('Content').asObject();
    if (contentObj) {
        result += convertJsonToHtml(contentObj);
    }

    result += closeTagHasSpace ? '{{ /' : '{{/';
    result += conditionalKey;
    result += '}}';

    // Output whitespace between positive and negative blocks if present
    const whitespaceBetween = conditionalBlock.get('WhitespaceBetween').asString();
    if (whitespaceBetween) {
        result += whitespaceBetween;
    }

    // Output negative block if present
    const negBlockValue = conditionalBlock.get('NegativeBlock');
    const negBlockStructure = negBlockValue.asObject();
    if (negBlockStructure) {
        const negCloseTagHasSpace = negBlockStructure.get('CloseTagHasSpace').asString() === 'true';
        result += '{{^';
        result += conditionalKey;
        result += '}}';
        const negContentObj = negBlockStructure.get('Content').asObject();
        if (negContentObj) {
            result += convertJsonToHtml(negContentObj);
        }
        result += negCloseTagHasSpace ? '{{ /' : '{{/';
        result += conditionalKey;
        result += '}}';
    }

    return result;
}
